import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import t from '../lib/I18n';
import config from '../config';
import appVersion from '../appVersion';
import reportError, {
    init as initErrorReporter
} from '../helpers/ErrorReporter';
import {
    Browsers,
    GlobalModals as GlobalModalType,
    IgnoredErrors,
    IgnoredErrorStacks,
    IgnoredPromiseRejectionStacks,
    IgnoredTargettedErrors
} from '../constants/AppConstants';
import { browserDetection } from '../helpers/DeviceDetection';

import ModalActions from '../actions/ModalActions';
import ErrorModal from './general/ErrorModal';

export const errorHandler = function(...args) {
    const e = args.length === 5 ? args.pop() : args.shift();

    // Unwrap the underlying error: native rejection events carry it in `reason`,
    // synthetic bridge events in `detail.reason`, error events in `error`.
    const rejectionReason = e.reason !== undefined ? e.reason : e.detail && e.detail.reason;
    const error = rejectionReason ?? e.error ?? e;
    let shouldRefresh = false;

    // Ignore patterns are written against "Name: message" (as Sentry titles them),
    // but Error#message alone carries no name — compose the two when matching.
    const unwrappedMessage =
        (error && typeof error.message === 'string' &&
            (typeof error.name === 'string' && error.name
                ? error.name + ': ' + error.message
                : error.message)) ||
        '';

    const message =
        unwrappedMessage ||
        (typeof e.message === 'string' && e.message) ||
        '';

    const stack =
        (e.error && typeof e.error.stack === 'string' && e.error.stack) ||
        (error && typeof error.stack === 'string' && error.stack) ||
        '';

    const rejectionStack =
        (rejectionReason && typeof rejectionReason.stack === 'string' && rejectionReason.stack) || '';

    const shouldIgnore =
        Boolean(message) &&
        IgnoredErrors.some(errorPattern => message.match(errorPattern));

    const shouldIgnoreStacks =
        Boolean(stack) &&
        IgnoredErrorStacks.some(errorPattern => stack.match(errorPattern));

    const shouldIgnoreTargettedError =
        Boolean(stack) && Boolean(message) &&
        IgnoredTargettedErrors.some(({ stack: stackPattern, message: messagePattern }) =>
            stackPattern.test(stack) && messagePattern.test(message)
        );

    const shouldIgnorePromiseRejectionStacks =
        Boolean(rejectionStack) &&
        IgnoredPromiseRejectionStacks.some(({ stack: stackPattern, shouldRefresh: refresh }) => {
            const matches = rejectionStack.match(stackPattern);
            if (matches && refresh) {
                shouldRefresh = true;
            }
            return matches;
        });

    const isRefreshTokenError = error && error.name && error.name === 'RefreshTokenError';

    const isCookiebotConsentRequestIssue = typeof rejectionReason === 'string'
        && rejectionReason.includes('Failed to fetch')
        && rejectionReason.includes('googletagmanager.com')
        && rejectionReason.includes('consent.cookiebot.com')
        && rejectionReason.includes('consentmode');

    if (!shouldIgnore &&
        !shouldIgnoreStacks &&
        !shouldIgnoreTargettedError &&
        !shouldIgnorePromiseRejectionStacks &&
        !isRefreshTokenError &&
        !isCookiebotConsentRequestIssue) {
        console.error('Error boundary caught: ', error);
        // Deferred to avoid re-entering the dispatcher if this fires from inside a dispatch.
        _.defer(() => ModalActions.open(GlobalModalType.ERROR, { error }));
    }

    if (shouldRefresh) {
        window.location.reload();
    }

    // Sentry's own handlers already capture native error events and unhandled
    // rejections — reporting those here double-counts every one of them. A raw
    // Event object must never be reported either: it stringifies to junk like
    // "[object PromiseRejectionEvent]".
    const hasEvent = typeof Event !== 'undefined';
    const isNativeErrorEvent = typeof ErrorEvent !== 'undefined' && e instanceof ErrorEvent;
    const isNativeRejectionEvent = hasEvent && e instanceof Event && 'reason' in e;
    const isRawEventError = hasEvent && error instanceof Event;

    if (error && !isNativeErrorEvent && !isNativeRejectionEvent && !isRawEventError) {
        reportError(error);
    }
};

export default class GlobalErrorBoundary extends React.Component {
    static propTypes = {
        children: PropTypes.any
    };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    state = {
        hasError: false,
        isRefreshing: false
    };

    componentDidMount() {
        const canUseSentrySDK = !config.get('debug') && browserDetection() !== Browsers.IOS_CHROME;

        if (canUseSentrySDK) {
            initErrorReporter(appVersion.version);
        }
        window.addEventListener('error', errorHandler);
        window.addEventListener('unhandledrejection', errorHandler);
    }

    componentDidCatch(error) {
        errorHandler(error);
    }

    componentWillUnmount() {
        window.removeEventListener('error', errorHandler);
        window.removeEventListener('unhandledrejection', errorHandler);
    }

    continue = () => {
        this.setState({ hasError: false });
    };

    refresh = () => {
        this.setState({ hasError: false, isRefreshing: true });
        window.location.reload();
    };

    render() {
        if (this.state.isRefreshing) {
            return (
                <div className="margin-top padding-top loading text-center loading--standalone">
                    <span className="otrl circle-spinner" />
                    <h2 className="no-margin">{t('global.loading')}</h2>
                </div>
            );
        }
        if (this.state.hasError) {
            return (
                <ErrorModal
                    closeText="button.text.continue"
                    onClose={ this.continue }
                    customButton={ {
                        text: 'button.text.refresh',
                        onClick: this.refresh
                    } }
                />
            );
        }
        return this.props.children;
    }
}
